import { useMemo } from 'react'

import { sortArrayBy } from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

const watchEntityKeys = ['pricingPlans']

interface Options {
  entityKey?: string,
  performHttpRequests?: boolean,
}

function usePricingPlans(options: Options = {}) {
  const { entityKey } = options

  const {
    updatedEntities: { pricingPlans: pricingPlansUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const pricingPlans = useSelector(reduxState => reduxState.entities.pricingPlans)

  const filteredPricingPlans = useMemo(() => {
    const filtered = Object.values(pricingPlans).filter(pricingPlan => pricingPlan)

    return sortArrayBy(filtered, 'asc', 'title')
  }, [pricingPlansUpdatedAt])

  const filteredPricingPlansCount = filteredPricingPlans.length
  const hasFilteredPricingPlans = !!filteredPricingPlansCount

  const loadMorePayload = useLoadMore({
    loadedCount: filteredPricingPlansCount,
    filters: {},
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingPricingPlans } = useReduxAction(
    'pricingPlans',
    'loadPricingPlans',
    {
      entityKey,
      ...filtersWithOffset,
      limit,
    },
    [performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredPricingPlans,
    filteredPricingPlansCount,
    hasFilteredPricingPlans,
    loading: loadingPricingPlans,
  }
}

export default usePricingPlans
